/**
 * Generate a default avatar SVG data URI from a display name or email.
 *
 * Produces a colored circle with up to 2 initials — no external CDN needed.
 * The background color is deterministically derived from the seed string
 * so the same user always gets the same color.
 */

// Curated palette — vibrant but not harsh, works on both light/dark
const PALETTE = [
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#a855f7", // purple
  "#ec4899", // pink
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#14b8a6", // teal
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#0ea5e9", // sky
]

function hashCode(str: string): number {
  let h = 0
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

/**
 * Extract up to 2 initials from a name string.
 * - "张彬彬" → "张彬"
 * - "John Doe" → "JD"
 * - "alice@example.com" → "A"
 * - "" → "?"
 */
function getInitials(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return "?"

  // If it looks like an email, use the part before @
  const localPart = trimmed.includes("@") ? trimmed.split("@")[0] : trimmed

  // Split by spaces / underscores / dots / hyphens
  const parts = localPart.split(/[\s._-]+/).filter(Boolean)

  if (parts.length >= 2) {
    // Take first char of first two parts
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }

  // Single word: for CJK take first 2 chars, otherwise first char
  const word = parts[0] || trimmed
  const isCJK = /[\u4e00-\u9fff\u3400-\u4dbf]/.test(word)
  if (isCJK && word.length >= 2) {
    return word.slice(0, 2)
  }
  return word[0].toUpperCase()
}

/**
 * Generate an avatar SVG as a data: URI.
 *
 * @param seed - username, email, phone, or any string
 * @param size - SVG viewport size (default 128)
 */
export function generateAvatarDataUri(seed: string, size = 128): string {
  const initials = getInitials(seed)
  const bg = PALETTE[hashCode(seed) % PALETTE.length]
  const fontSize = initials.length > 1 ? size * 0.38 : size * 0.48

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  <rect width="${size}" height="${size}" rx="${size / 2}" fill="${bg}"/>
  <text x="50%" y="50%" dy=".1em" fill="#fff" font-family="system-ui,-apple-system,sans-serif" font-size="${fontSize}" font-weight="600" text-anchor="middle" dominant-baseline="central">${initials}</text>
</svg>`

  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

/**
 * Get a display name from whatever user info is available.
 * Priority: username > email local part > phone (masked) > "用户"
 */
export function getDisplayName(info: {
  username?: string
  email?: string
  phone?: string
}): string {
  if (info.username) return info.username
  if (info.email) {
    const local = info.email.split("@")[0]
    return local || "用户"
  }
  if (info.phone) {
    // Mask phone: 138****1234
    if (info.phone.length >= 7) {
      return info.phone.slice(0, 3) + "****" + info.phone.slice(-4)
    }
    return info.phone
  }
  return "用户"
}
